import { useCallback, useMemo, useState } from 'react'

/**
 * Filtre de carte par type d'île, état purement front (aucune donnée serveur).
 * Le `Set` contient les clés de légende ACTIVES. **Vide = tout visible** (état par
 * défaut, ce n'est PAS un filtre « rien »). Cf. `islandLegendKey` (pages/Map) pour
 * la clé d'une île ; les boutons de `MapLegend` togglent ces mêmes clés.
 */
export interface MapTypeFilter {
  /** Clés de légende actuellement sélectionnées (vide = tout visible). Lecture seule. */
  active: ReadonlySet<string>
  /** Le bouton de cette clé est-il enfoncé (sélectionné) ? */
  isActive: (key: string) => boolean
  /** Une île de cette clé doit-elle ressortir (pleine/cliquable) ? */
  isVisible: (key: string) => boolean
  /** (Dé)sélectionne une clé. */
  toggle: (key: string) => void
  /** Réinitialise à « tout visible » (set vide). */
  reset: () => void
  /** Remplace les clés actives (ex. deeplink `?type=`). Set vide = tout visible. */
  set: (keys: string[]) => void
}

/** Règle pure : set vide → tout visible ; sinon seules les clés actives ressortent. */
export function isTypeVisible(active: Set<string>, key: string): boolean {
  return active.size === 0 || active.has(key)
}

export function useMapTypeFilter(): MapTypeFilter {
  const [active, setActive] = useState<Set<string>>(() => new Set())

  const toggle = useCallback((key: string) => {
    setActive((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const reset = useCallback(() => setActive(new Set()), [])
  const set = useCallback((keys: string[]) => setActive(new Set(keys)), [])

  return useMemo(
    () => ({
      active,
      isActive: (key: string) => active.has(key),
      isVisible: (key: string) => isTypeVisible(active, key),
      toggle,
      reset,
      set,
    }),
    [active, toggle, reset, set],
  )
}
